import { useState, useEffect, useCallback } from 'react';
import WorldMap from '../components/WorldMap';
import ModPanel from '../components/ModPanel';
import { moderateText } from '../utils/moderateText';
import { fetchPosts, addPost, approvePost, deletePost, heartPost } from '../firebase/posts';
import './Forum.css';

const ADMIN_PASSWORD = 'Elf2017!!!';

const CATEGORIES = [
  'I have a sibling with special needs',
  'I have a family member with special needs',
  'I have a classmate with special needs',
  'I want to learn more',
  'I have special needs myself',
  'I\'m a parent or caregiver',
  'Something else',
];

const COUNTRIES = [
  { name: 'United States',  lat: 38,   lng: -97   },
  { name: 'United Kingdom', lat: 54,   lng: -2    },
  { name: 'Canada',         lat: 56,   lng: -96   },
  { name: 'Australia',      lat: -25,  lng: 133   },
  { name: 'Germany',        lat: 51,   lng: 10    },
  { name: 'France',         lat: 46,   lng: 2     },
  { name: 'Brazil',         lat: -10,  lng: -51   },
  { name: 'India',          lat: 20,   lng: 77    },
  { name: 'Japan',          lat: 36,   lng: 138   },
  { name: 'South Africa',   lat: -29,  lng: 25    },
  { name: 'Mexico',         lat: 23,   lng: -102  },
  { name: 'Spain',          lat: 40,   lng: -4    },
  { name: 'Italy',          lat: 42,   lng: 12    },
  { name: 'Netherlands',    lat: 52,   lng: 5     },
  { name: 'New Zealand',    lat: -41,  lng: 174   },
  { name: 'Sweden',         lat: 62,   lng: 16    },
  { name: 'Norway',         lat: 64,   lng: 17    },
  { name: 'Ireland',        lat: 53,   lng: -8    },
  { name: 'Argentina',      lat: -34,  lng: -64   },
  { name: 'Nigeria',        lat: 10,   lng: 8     },
  { name: 'Kenya',          lat: 0,    lng: 37    },
  { name: 'Philippines',    lat: 13,   lng: 122   },
  { name: 'Singapore',      lat: 1.3,  lng: 103.8 },
  { name: 'Other',          lat: null, lng: null  },
];

// Well-known cities → coordinates. Falls back to country coords if city not found.
const CITY_COORDS = {
  'new york': { lat: 40.71, lng: -74.01 },
  'los angeles': { lat: 34.05, lng: -118.24 },
  'chicago': { lat: 41.88, lng: -87.63 },
  'houston': { lat: 29.76, lng: -95.37 },
  'phoenix': { lat: 33.45, lng: -112.07 },
  'philadelphia': { lat: 39.95, lng: -75.17 },
  'san antonio': { lat: 29.42, lng: -98.49 },
  'san diego': { lat: 32.72, lng: -117.16 },
  'dallas': { lat: 32.78, lng: -96.80 },
  'san francisco': { lat: 37.77, lng: -122.42 },
  'seattle': { lat: 47.61, lng: -122.33 },
  'denver': { lat: 39.74, lng: -104.98 },
  'miami': { lat: 25.77, lng: -80.19 },
  'atlanta': { lat: 33.75, lng: -84.39 },
  'boston': { lat: 42.36, lng: -71.06 },
  'london': { lat: 51.51, lng: -0.13 },
  'manchester': { lat: 53.48, lng: -2.24 },
  'birmingham': { lat: 52.48, lng: -1.90 },
  'toronto': { lat: 43.65, lng: -79.38 },
  'vancouver': { lat: 49.25, lng: -123.12 },
  'montreal': { lat: 45.50, lng: -73.57 },
  'calgary': { lat: 51.05, lng: -114.07 },
  'sydney': { lat: -33.87, lng: 151.21 },
  'melbourne': { lat: -37.81, lng: 144.96 },
  'brisbane': { lat: -27.47, lng: 153.02 },
  'perth': { lat: -31.95, lng: 115.86 },
  'berlin': { lat: 52.52, lng: 13.40 },
  'munich': { lat: 48.14, lng: 11.58 },
  'hamburg': { lat: 53.55, lng: 9.99 },
  'paris': { lat: 48.85, lng: 2.35 },
  'lyon': { lat: 45.75, lng: 4.84 },
  'marseille': { lat: 43.30, lng: 5.37 },
  'madrid': { lat: 40.42, lng: -3.70 },
  'barcelona': { lat: 41.39, lng: 2.15 },
  'rome': { lat: 41.90, lng: 12.50 },
  'milan': { lat: 45.46, lng: 9.19 },
  'amsterdam': { lat: 52.37, lng: 4.90 },
  'stockholm': { lat: 59.33, lng: 18.07 },
  'oslo': { lat: 59.91, lng: 10.75 },
  'dublin': { lat: 53.33, lng: -6.25 },
  'tokyo': { lat: 35.68, lng: 139.69 },
  'osaka': { lat: 34.69, lng: 135.50 },
  'mumbai': { lat: 19.08, lng: 72.88 },
  'delhi': { lat: 28.61, lng: 77.21 },
  'bangalore': { lat: 12.97, lng: 77.59 },
  'sao paulo': { lat: -23.55, lng: -46.63 },
  'rio de janeiro': { lat: -22.91, lng: -43.17 },
  'buenos aires': { lat: -34.60, lng: -58.38 },
  'mexico city': { lat: 19.43, lng: -99.13 },
  'cape town': { lat: -33.93, lng: 18.42 },
  'johannesburg': { lat: -26.20, lng: 28.04 },
  'nairobi': { lat: -1.29, lng: 36.82 },
  'lagos': { lat: 6.52, lng: 3.38 },
  'singapore': { lat: 1.35, lng: 103.82 },
  'manila': { lat: 14.60, lng: 120.98 },
  'auckland': { lat: -36.85, lng: 174.76 },
  'wellington': { lat: -41.29, lng: 174.78 },
};

function getCoordsForPost(form, countryData) {
  if (form.city) {
    const key = form.city.trim().toLowerCase();
    if (CITY_COORDS[key]) return CITY_COORDS[key];
  }
  return { lat: countryData?.lat ?? null, lng: countryData?.lng ?? null };
}

function timeAgo(val) {
  if (!val) return '';
  // Firestore Timestamp or plain date string
  const date = val?.toDate ? val.toDate() : new Date(val);
  const diff  = Math.floor((new Date() - date) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 30)  return `${diff} days ago`;
  return `${Math.floor(diff / 30)} month${Math.floor(diff / 30) > 1 ? 's' : ''} ago`;
}

const cardColors = ['lavender','mint','peach','blue','lavender','mint','peach','blue'];

export default function Forum() {
  const [posts, setPosts]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [filter, setFilter]           = useState('All');
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState({ name:'', category:CATEGORIES[0], message:'', country:'', city:'' });
  const [formError, setFormError]     = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);

  // Mod state
  const [showModLogin, setShowModLogin]   = useState(false);
  const [showModPanel, setShowModPanel]   = useState(false);
  const [isAdmin, setIsAdmin]             = useState(false);
  const [modPassword, setModPassword]     = useState('');
  const [modPasswordError, setModPasswordError] = useState('');

  // ── Load posts ─────────────────────────────────────────────
  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await fetchPosts();
      // Override stored country-level coords with city coords if we know the city
      const enriched = data.map(p => {
        if (!p.city) return p;
        const key = p.city.trim().toLowerCase();
        if (CITY_COORDS[key]) return { ...p, lat: CITY_COORDS[key].lat, lng: CITY_COORDS[key].lng };
        return p;
      });
      setPosts(enriched);
    } catch (err) {
      console.error(err);
      setLoadError('Couldn\'t load stories right now. Please refresh! 🌸');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);

  const approved = posts.filter(p => p.status === 'approved');
  const pending  = posts.filter(p => p.status === 'pending');
  const filtered = filter === 'All' ? approved : approved.filter(p => p.category === filter);

  // ── Submit story ───────────────────────────────────────────
  function handleFormChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setFormError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Please enter your name! 😊'); return; }
    const check = moderateText(form.message);
    if (!check.ok) { setFormError(check.reason); return; }

    const countryData = COUNTRIES.find(c => c.name === form.country);
    const coords = getCoordsForPost(form, countryData);
    setSubmitting(true);
    try {
      const id = await addPost({ ...form, lat: coords.lat, lng: coords.lng });
      // Optimistically add to local list (won't show publicly — it's pending)
      setPosts(prev => [{ ...form, id, status:'pending', hearts:0, createdAt: new Date().toISOString(),
        lat: coords.lat, lng: coords.lng }, ...prev]);
      setForm({ name:'', category:CATEGORIES[0], message:'', country:'', city:'' });
      setShowForm(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 6000);
    } catch (err) {
      console.error(err);
      setFormError('Something went wrong. Please try again! 😔');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Heart a post ───────────────────────────────────────────
  async function handleHeart(id, currentHearts) {
    setPosts(prev => prev.map(p => p.id === id ? { ...p, hearts: p.hearts + 1 } : p));
    try { await heartPost(id, currentHearts); }
    catch (err) { console.error(err); }
  }

  // ── Mod auth ───────────────────────────────────────────────
  function handleModLogin(e) {
    e.preventDefault();
    if (modPassword === ADMIN_PASSWORD) {
      setIsAdmin(true); setShowModLogin(false);
      setShowModPanel(true); setModPassword(''); setModPasswordError('');
    } else {
      setModPasswordError('Wrong password! Try again. 🔑');
    }
  }

  // ── Mod actions ────────────────────────────────────────────
  async function handleApprove(id) {
    try {
      await approvePost(id);
      setPosts(prev => prev.map(p => p.id === id ? { ...p, status:'approved' } : p));
    } catch (err) { console.error(err); alert('Couldn\'t approve. Try again!'); }
  }

  async function handleReject(id) {
    try {
      await deletePost(id);
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (err) { console.error(err); alert('Couldn\'t remove post. Try again!'); }
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="forum fade-in">
      <section className="forum-hero">
        <div className="container">
          <div className="forum-hero-top">
            <div>
              <h1 className="section-title">💬 Share Your Story</h1>
              <p className="section-subtitle">
                A kind, safe space for kids to share their experiences with special needs 💜
              </p>
            </div>
            <button className="mod-access-btn"
              onClick={() => isAdmin ? setShowModPanel(true) : setShowModLogin(true)}
              title="Moderation panel" aria-label="Open moderation panel">
              🛡️ {pending.length > 0 && <span className="mod-badge">{pending.length}</span>}
            </button>
          </div>
          <div className="forum-rules-bar">
            <span>💜 Be kind</span>
            <span>🌟 Be honest</span>
            <span>🤝 Be respectful</span>
            <span>🌈 Celebrate everyone</span>
            <span className="rule-note">Unkind posts will not be approved</span>
          </div>
        </div>
      </section>

      <div className="container forum-body">
        {submitted && (
          <div className="success-banner">
            <span>🎉</span>
            <div>
              <strong>Your story was submitted!</strong>
              <p>It's waiting for Emmi to review. Once approved it will appear on the map! 💜</p>
            </div>
          </div>
        )}

        {/* World Map */}
        <WorldMap posts={approved} />

        {/* Top bar */}
        <div className="forum-topbar">
          <div className="forum-filter">
            <span className="filter-label">Filter:</span>
            <select value={filter} onChange={e => setFilter(e.target.value)} className="forum-select">
              <option value="All">All Stories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" onClick={() => setShowForm(v => !v)}>
            {showForm ? '✕ Cancel' : '✏️ Share Your Story'}
          </button>
        </div>

        {/* Post form */}
        {showForm && (
          <div className="post-form-wrapper card">
            <h3>📝 Share Your Story</h3>
            <p className="form-note">
              Be kind and honest! Stories are reviewed by Emmi before they appear on the site.
              Your pin will show up on the world map once it's approved. 🌍
            </p>
            <form onSubmit={handleSubmit} className="post-form">
              <div className="form-row-2">
                <div className="form-group">
                  <label>Your first name *</label>
                  <input name="name" value={form.name} onChange={handleFormChange}
                    placeholder="e.g. Lily" className="form-input" maxLength={30} />
                </div>
                <div className="form-group">
                  <label>I am... *</label>
                  <select name="category" value={form.category} onChange={handleFormChange} className="form-input">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>📍 Your country (for the map)</label>
                  <select name="country" value={form.country} onChange={handleFormChange} className="form-input">
                    <option value="">Select your country…</option>
                    {COUNTRIES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>City (optional)</label>
                  <input name="city" value={form.city} onChange={handleFormChange}
                    placeholder="e.g. Sydney" className="form-input" maxLength={50} />
                </div>
              </div>
              <div className="form-group">
                <label>Your story or message *</label>
                <textarea name="message" value={form.message} onChange={handleFormChange}
                  placeholder="Share your experience, what you've learned, or how special needs has touched your life..."
                  className="form-input form-textarea" rows={4} maxLength={1000} />
                <span className="char-count">{form.message.length}/1000</span>
              </div>
              {formError && (
                <div className="form-error-box">
                  <span>⚠️</span><p>{formError}</p>
                </div>
              )}
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Sending… ⏳' : 'Share My Story 💜'}
              </button>
            </form>
          </div>
        )}

        {/* Stories */}
        {loading ? (
          <div className="events-loading">
            <span className="loading-spinner">🌸</span>
            <p>Loading stories…</p>
          </div>
        ) : loadError ? (
          <div className="events-error">
            <span>😔</span><p>{loadError}</p>
            <button className="btn btn-outline" onClick={loadPosts}>Try Again</button>
          </div>
        ) : (
          <>
            <div className="posts-count">
              <strong>{filtered.length}</strong> {filtered.length === 1 ? 'story' : 'stories'}
              {filter !== 'All' ? ` in "${filter}"` : ' shared'}
            </div>
            <div className="posts-grid">
              {filtered.map((post, i) => {
                const color = cardColors[i % cardColors.length];
                return (
                  <div key={post.id} className={`post-card card post-card-${color}`}>
                    {post.isCreator && <div className="creator-badge">⭐ Site Creator</div>}
                    <div className="post-header">
                      <div className={`post-avatar post-avatar-${color}`}>{post.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <strong className="post-name">{post.name}</strong>
                        <div className="post-date">
                          {post.city ? `${post.city}, ` : ''}{post.country || ''}
                          {(post.city || post.country) ? ' · ' : ''}{timeAgo(post.createdAt)}
                        </div>
                      </div>
                      <span className={`post-category-tag tag tag-${color}`}>{post.category}</span>
                    </div>
                    <p className="post-message">"{post.message}"</p>
                    <div className="post-footer">
                      <button className="heart-btn" onClick={() => handleHeart(post.id, post.hearts)}
                        aria-label="Give a heart">💜 {post.hearts}</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {filtered.length === 0 && (
              <div className="no-posts">
                <span>💬</span>
                <p>No stories yet. Be the first to share!</p>
                <button className="btn btn-primary" onClick={() => setShowForm(true)}>Share Your Story</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mod login */}
      {showModLogin && (
        <div className="modal-overlay" onClick={() => setShowModLogin(false)}>
          <div className="modal card" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModLogin(false)}>✕</button>
            <span className="modal-emoji">🛡️</span>
            <h3>Moderation Access</h3>
            <p>Only Emmi can review stories! Enter the admin password:</p>
            <form onSubmit={handleModLogin} className="password-form">
              <input type="password" value={modPassword} onChange={e => setModPassword(e.target.value)}
                placeholder="Enter password…" className="form-input" autoFocus />
              {modPasswordError && <p className="form-error">{modPasswordError}</p>}
              <button type="submit" className="btn btn-primary">Enter 🔓</button>
            </form>
          </div>
        </div>
      )}

      {/* Mod panel */}
      {showModPanel && (
        <ModPanel pending={pending} onApprove={handleApprove} onReject={handleReject}
          onClose={() => setShowModPanel(false)} />
      )}
    </div>
  );
}
